import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import {
  UseWaterfallUrlDefaultsParams,
  UseWaterfallUrlDefaultsResult,
} from './useWaterfallUrlDefaults.props';

export type { UseWaterfallUrlDefaultsParams, UseWaterfallUrlDefaultsResult } from './useWaterfallUrlDefaults.props';

/**
 * Handles one-time initialization of URL defaults.
 *
 * This hook watches for when required data becomes available and sets
 * missing URL params to their default values. It only runs once per
 * "session" (tracked via refs), and resets when company changes.
 *
 * Flow:
 * 1. Wait for measurement dates to load
 * 2. If no measurement date in URL, set default (most recent)
 * 3. Wait for cap table list to load
 * 4. If no cap table in URL, set default (primary)
 * 5. If no exit date in URL, set default (measurement date or today)
 */
export const useWaterfallUrlDefaults = ({
  urlState,
  isFirmPage,
  mostRecentMeasurementDate,
  primaryCaptable,
  companyMeasurementDates,
  capTableList,
  isMeasurementDatesLoading,
  isCapTableListLoading,
  hasMeasurementDatesSettled,
  hasCapTableListSettled,
  companyIdFromRoute,
  selectedMeasurementDateFromRoute,
}: UseWaterfallUrlDefaultsParams): UseWaterfallUrlDefaultsResult => {
  // Track initialization state - refs prevent double-runs, state triggers re-renders
  const hasInitializedMeasurementDateRef = useRef(false);
  const hasInitializedCaptableRef = useRef(false);
  const hasInitializedExitDateRef = useRef(false);
  const lastCompanyIdRef = useRef<number | undefined>(undefined);
  const lastMeasurementDateValueRef = useRef<string | undefined>(undefined);

  // State to trigger re-renders when initialization completes
  const [measurementDateInitialized, setMeasurementDateInitialized] = useState(false);
  const [captableInitialized, setCaptableInitialized] = useState(false);
  const [exitDateInitialized, setExitDateInitialized] = useState(false);

  // Determine the effective company ID
  const effectiveCompanyId = isFirmPage ? urlState.companyIdFromUrl : companyIdFromRoute;

  // Reset initialization flags when company changes
  useEffect(() => {
    if (effectiveCompanyId !== lastCompanyIdRef.current) {
      // Company changed - reset all initialization flags
      hasInitializedMeasurementDateRef.current = false;
      hasInitializedCaptableRef.current = false;
      hasInitializedExitDateRef.current = false;
      setMeasurementDateInitialized(false);
      setCaptableInitialized(false);
      setExitDateInitialized(false);
      lastCompanyIdRef.current = effectiveCompanyId;
      lastMeasurementDateValueRef.current = undefined;
    }
  }, [effectiveCompanyId]);

  // ============================================================
  // URL Date Validation on Company Switch
  // ============================================================
  // When switching between companies on the firm waterfall page, the URL preserves
  // the date parameter (e.g., date=2024-08-31). However, the new company may not
  // have a measurement date with that exact date value. This effect ensures that
  // when a company is selected and its measurement dates load, if the URL date
  // doesn't exist for that company, we update the URL to use the most recent
  // measurement date available for the new company. This keeps the URL as the
  // single source of truth and prevents the UI from showing an invalid state.
  useEffect(() => {
    if (!isFirmPage) {
      return;
    }
    if (isMeasurementDatesLoading) {
      return;
    }
    if (!effectiveCompanyId || !companyMeasurementDates.length) {
      return;
    }

    const urlDateValue = urlState.measurementDateValueFromUrl;
    if (!urlDateValue) {
      return;
    }

    const matchingDate = companyMeasurementDates.find(
      md => md.date === urlDateValue
    );

    if (!matchingDate && mostRecentMeasurementDate) {
      urlState.setMeasurementDate(mostRecentMeasurementDate, { clearCaptable: true });
    }
  }, [
    isFirmPage,
    effectiveCompanyId,
    companyMeasurementDates,
    isMeasurementDatesLoading,
    urlState.measurementDateValueFromUrl,
    mostRecentMeasurementDate,
    urlState,
  ]);

  // Reset cap table initialization when measurement date changes
  // This ensures we default to primary cap table when user changes measurement date
  useEffect(() => {
    const currentMeasurementDateValue = urlState.measurementDateValueFromUrl;

    // Skip on initial undefined -> undefined (no change)
    if (currentMeasurementDateValue === undefined && lastMeasurementDateValueRef.current === undefined) {
      return;
    }

    if (currentMeasurementDateValue !== lastMeasurementDateValueRef.current) {
      // Measurement date changed - reset cap table initialization if we had a previous value
      if (lastMeasurementDateValueRef.current !== undefined) {
        hasInitializedCaptableRef.current = false;
        setCaptableInitialized(false);
      }
      lastMeasurementDateValueRef.current = currentMeasurementDateValue;
    }
  }, [urlState.measurementDateValueFromUrl]);

  // Reset cap table initialization when a date/company transition clears it from the URL.
  const lastCaptableIdRef = useRef<number | undefined>(urlState.captableIdFromUrl);
  useEffect(() => {
    const currentCaptableId = urlState.captableIdFromUrl;
    const previousCaptableId = lastCaptableIdRef.current;

    // If cap table went from a value to undefined, reset initialization
    if (previousCaptableId !== undefined && currentCaptableId === undefined) {
      hasInitializedCaptableRef.current = false;
      setCaptableInitialized(false);
    }

    lastCaptableIdRef.current = currentCaptableId;
  }, [urlState.captableIdFromUrl]);

  // Initialize measurement date default (only on first load, validation effect handles company changes)
  useEffect(() => {
    // Skip if still loading
    if (isMeasurementDatesLoading) {
      return;
    }

    // for company pages: Always use measurement date from route context
    if (!isFirmPage) {
      // On company pages, there is no dropdown - measurement date comes from company context
      // This value is already in state and doesn't change
      if (selectedMeasurementDateFromRoute && !hasInitializedMeasurementDateRef.current) {
        urlState.setMeasurementDate(selectedMeasurementDateFromRoute);
        hasInitializedMeasurementDateRef.current = true;
        setMeasurementDateInitialized(true);
      }
      return;
    }
    // For firm pages, need a company selected first
    if (!effectiveCompanyId) {
      return;
    }

    // Need measurement dates to be loaded
    if (!companyMeasurementDates.length) {
      return;
    }

    // Skip if already initialized
    if (hasInitializedMeasurementDateRef.current) {
      return;
    }

    // If URL already has a date, mark as initialized (validation effect will handle updating if needed)
    if (urlState.measurementDateValueFromUrl) {
      hasInitializedMeasurementDateRef.current = true;
      setMeasurementDateInitialized(true);
      return;
    }

    // No date in URL - set most recent
    if (mostRecentMeasurementDate) {
      urlState.setMeasurementDate(mostRecentMeasurementDate);
      hasInitializedMeasurementDateRef.current = true;
      setMeasurementDateInitialized(true);
    }
  }, [
    urlState,
    isFirmPage,
    effectiveCompanyId,
    isMeasurementDatesLoading,
    companyMeasurementDates,
    mostRecentMeasurementDate,
    selectedMeasurementDateFromRoute,
    urlState.measurementDateValueFromUrl,
  ]);

  // Initialize cap table default
  useEffect(() => {
    // Skip if still loading
    if (isCapTableListLoading) {
      return;
    }
    if (!capTableList.length) {
      return;
    }

    // For firm pages, need a company selected first
    if (isFirmPage && !effectiveCompanyId) {
      return;
    }

    // Need measurement date to be set first
    if (!urlState.measurementDateValueFromUrl && !measurementDateInitialized) {
      return;
    }

    // If URL already has a cap table, mark as initialized and done
    if (urlState.captableIdFromUrl) {
      hasInitializedCaptableRef.current = true;
      setCaptableInitialized(true);
      return;
    }

    // Skip if already initialized - only set defaults on first load or after reset
    // (company change or measurement date change resets the ref)
    if (hasInitializedCaptableRef.current) {
      return;
    }

    if (primaryCaptable) {
      urlState.setCaptable(primaryCaptable);
      hasInitializedCaptableRef.current = true;
      setCaptableInitialized(true);
    }
  }, [
    urlState,
    isFirmPage,
    effectiveCompanyId,
    isCapTableListLoading,
    capTableList.length,
    primaryCaptable,
    measurementDateInitialized,
    urlState.captableIdFromUrl,
    urlState.measurementDateValueFromUrl,
  ]);

  // Initialize exit date default
  useEffect(() => {
    // Skip if already initialized
    if (hasInitializedExitDateRef.current) {
      return;
    }

    // If URL already has an exit date, mark as initialized
    if (urlState.exitDateFromUrl) {
      hasInitializedExitDateRef.current = true;
      setExitDateInitialized(true);
      return;
    }

    // For firm pages without a company, set exit date to today so the input sheet renders
    // (The input sheet needs exitDate to render, even just to show the company selector)
    if (isFirmPage && !effectiveCompanyId) {
      urlState.setExitDate(moment().format(MOMENT_DEFAULT_DATE_FORMAT));
      hasInitializedExitDateRef.current = true;
      setExitDateInitialized(true);
      return;
    }

    // For pages with a company, wait for measurement date to be initialized first
    if (!measurementDateInitialized && !urlState.measurementDateValueFromUrl) {
      return;
    }

    // Set default exit date (measurement date or today)
    const resolvedMeasurementDate = companyMeasurementDates.find(
      md => md.date === urlState.measurementDateValueFromUrl
    );

    const defaultExitDate = resolvedMeasurementDate?.date
      ? moment(resolvedMeasurementDate.date).format(MOMENT_DEFAULT_DATE_FORMAT)
      : moment().format(MOMENT_DEFAULT_DATE_FORMAT);

    urlState.setExitDate(defaultExitDate);
    hasInitializedExitDateRef.current = true;
    setExitDateInitialized(true);
  }, [
    urlState,
    isFirmPage,
    effectiveCompanyId,
    companyMeasurementDates,
    measurementDateInitialized,
  ]);

  // Determine if fully initialized (use state to trigger re-renders)
  const isInitialized = useMemo(
    () => measurementDateInitialized && captableInitialized && exitDateInitialized,
    [measurementDateInitialized, captableInitialized, exitDateInitialized]
  );

  // A settled empty response cannot produce URL defaults and must return control to the Input.
  const isInitializationUnavailable = useMemo(() => {
    if (!effectiveCompanyId || !hasMeasurementDatesSettled) {
      return false;
    }

    if ((isFirmPage && !companyMeasurementDates.length) || (!isFirmPage && !selectedMeasurementDateFromRoute)) {
      return true;
    }

    if (!hasCapTableListSettled) {
      return false;
    }

    if (!capTableList.length) {
      return true;
    }

    return !urlState.captableIdFromUrl && !primaryCaptable;
  }, [
    capTableList.length,
    companyMeasurementDates.length,
    effectiveCompanyId,
    hasCapTableListSettled,
    hasMeasurementDatesSettled,
    isFirmPage,
    primaryCaptable,
    selectedMeasurementDateFromRoute,
    urlState.captableIdFromUrl,
  ]);

  // Determine if we're still initializing (data is loading or defaults not yet set)
  // For firm pages without a company selected, we're NOT initializing - we're waiting for user input
  const isInitializing = useMemo(() => {
    // If no company is selected (firm page without URL company), we're not initializing
    // We should show the UI so user can select a company
    if (!effectiveCompanyId) {
      return false;
    }

    // Keep the handoff active between settled requests and the effects that write URL defaults.
    return !isInitialized && !isInitializationUnavailable;
  }, [effectiveCompanyId, isInitialized, isInitializationUnavailable]);

  const reset = useCallback(() => {
    hasInitializedMeasurementDateRef.current = false;
    hasInitializedCaptableRef.current = false;
    hasInitializedExitDateRef.current = false;
    setMeasurementDateInitialized(false);
    setCaptableInitialized(false);
    setExitDateInitialized(false);
  }, []);

  return { isInitialized, isInitializing, isInitializationUnavailable, reset };
};
